import moment from "moment";
import React, { useEffect, useState } from "react";
import { Button, Form, Spinner, Table } from "react-bootstrap";
import { useDispatch } from "react-redux";
import {
  applyNoc,
  getMyNoc,
  getNocTypes,
  payNocSuccess,
} from "../../../store/actions/studentAction/nocAction";
import { NOC_DIGITAL_NOTE, openNocCheckout } from "../../../utils/nocPay";
import { errorAlert, successAlert } from "../../../utils/alert";

/*
  No Objection Certificate, in the student's own login.

  The student picks a NOC type the council offers and a validity (3 or 6
  months), pays the fee set for that validity, and the NOC is issued and
  available as a PDF straight away. A duplicate, or a record missing details,
  is rejected before any payment.
*/
const StudentNoc = () => {
  const dispatch = useDispatch();

  const [types, setTypes] = useState([]);
  const [eligibility, setEligibility] = useState(null);
  const [requests, setRequests] = useState([]);
  const [typeId, setTypeId] = useState("");
  const [validity, setValidity] = useState("3");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const load = async () => {
    const [typeRes, myList] = await Promise.all([
      dispatch(getNocTypes()),
      dispatch(getMyNoc()),
    ]);
    setTypes(Array.isArray(typeRes?.types) ? typeRes.types : []);
    setEligibility(typeRes?.eligibility || null);
    setRequests(Array.isArray(myList) ? myList : []);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const selected = types.find((t) => String(t.id) === String(typeId));
  const fee = selected
    ? Number(validity) === 6
      ? Number(selected.fee_6m)
      : Number(selected.fee_3m)
    : 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!typeId) {
      errorAlert("Please choose a NOC type.");
      return;
    }
    setSending(true);

    const body = new FormData();
    body.append("noc_type_id", typeId);
    body.append("validity_months", validity);
    const res = await dispatch(applyNoc(body));

    // Rejected up-front (duplicate or missing details) - no payment taken.
    if (res.status !== "success") {
      errorAlert(res.message || "The NOC could not be applied for.");
      setSending(false);
      return;
    }

    const d = res.data || {};

    // Free NOC - issued immediately.
    if (d.free) {
      successAlert("NOC issued.");
      setTypeId("");
      await load();
      setSending(false);
      return;
    }

    // Paid NOC - open Razorpay, then confirm.
    const pay = await openNocCheckout(d);
    if (pay.payment_id) {
      const fd = new FormData();
      fd.append("noc_id", d.noc_id);
      fd.append("razorpay_payment_id", pay.payment_id);
      const done = await dispatch(payNocSuccess(fd));
      if (done.status === "success") {
        successAlert("Payment verified. NOC issued.");
        setTypeId("");
        await load();
      } else {
        errorAlert(done.message || "Payment could not be verified.");
      }
    } else if (pay.error) {
      errorAlert(pay.error);
    }
    setSending(false);
  };

  const statusCell = (r) => {
    if (Number(r.status) === 1) return <span className="text-success">Issued</span>;
    if (Number(r.status) === 2) return <span className="text-danger">Rejected</span>;
    return <span className="text-info">Payment Pending</span>;
  };

  return (
    <div className="Title-box">
      <div className="row">
        <div className="col-md-6">
          <div className="BigTitle">Apply for NOC</div>
        </div>
      </div>

      {loading ? (
        <div className="text-center mt-4">
          <Spinner animation="border" role="status" size="lg">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      ) : (
        <>
          <div className="mid-content-box mt-3">
            {eligibility && eligibility.ok === false ? (
              <div className="alert alert-warning mb-0">{eligibility.reason}</div>
            ) : types.length === 0 ? (
              <p>No NOC type is available right now. Please contact the council.</p>
            ) : (
              <Form onSubmit={submit}>
                <div className="row">
                  <div className="col-md-6">
                    <Form.Group className="mb-3">
                      <Form.Label>Purpose (NOC Type)</Form.Label>
                      <Form.Control
                        as="select"
                        value={typeId}
                        onChange={(e) => setTypeId(e.target.value)}
                      >
                        <option value="">-- Select NOC type --</option>
                        {types.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.subject} (Form {t.form_no})
                          </option>
                        ))}
                      </Form.Control>
                    </Form.Group>
                  </div>

                  <div className="col-md-6">
                    <Form.Group className="mb-3">
                      <Form.Label>Validity</Form.Label>
                      <div>
                        <Form.Check
                          inline
                          type="radio"
                          id="noc-v3"
                          label={
                            "03 Months" +
                            (selected ? ` (₹${Number(selected.fee_3m)})` : "")
                          }
                          checked={validity === "3"}
                          onChange={() => setValidity("3")}
                        />
                        <Form.Check
                          inline
                          type="radio"
                          id="noc-v6"
                          label={
                            "06 Months" +
                            (selected ? ` (₹${Number(selected.fee_6m)})` : "")
                          }
                          checked={validity === "6"}
                          onChange={() => setValidity("6")}
                        />
                      </div>
                    </Form.Group>
                  </div>
                </div>

                {selected && (
                  <p className="mb-2">
                    Amount payable : <b>₹{fee}</b>
                  </p>
                )}

                <p className="text-muted" style={{ fontSize: "13px" }}>
                  {NOC_DIGITAL_NOTE}
                </p>

                <Button
                  type="submit"
                  disabled={sending || !typeId}
                  className="btn btn-comman"
                >
                  {sending
                    ? "Please wait..."
                    : fee > 0
                    ? `Apply & Pay ₹${fee}`
                    : "Apply for NOC"}
                </Button>
              </Form>
            )}
          </div>

          <div className="mid-content-box mt-4">
            <div className="BigTitle mb-2">My NOC Requests</div>
            <Table responsive>
              <thead>
                <tr>
                  <th>Sr No.</th>
                  <th>Purpose</th>
                  <th>Validity</th>
                  <th>Fee</th>
                  <th>Applied On</th>
                  <th>Status</th>
                  <th>NOC No.</th>
                  <th>Valid Until</th>
                  <th>Certificate</th>
                </tr>
              </thead>
              <tbody>
                {requests.length === 0 ? (
                  <tr>
                    <td colSpan={9}>You have not applied for any NOC yet.</td>
                  </tr>
                ) : (
                  requests.map((r, i) => (
                    <tr key={r.id}>
                      <td>{i + 1}</td>
                      <td>{r.purpose || "--"}</td>
                      <td>{r.validity_months ? `${r.validity_months} months` : "--"}</td>
                      <td>{r.fee != null ? `₹${Number(r.fee)}` : "--"}</td>
                      <td>
                        {r.created_at
                          ? moment(r.created_at).format("DD/MM/YYYY")
                          : "--"}
                      </td>
                      <td>{statusCell(r)}</td>
                      <td>{r.noc_number || "--"}</td>
                      <td>
                        {r.valid_until
                          ? moment(r.valid_until).format("DD/MM/YYYY")
                          : "--"}
                      </td>
                      <td>
                        {r.link ? (
                          <>
                            <a
                              href={r.link}
                              target="_blank"
                              rel="noreferrer"
                              className="btn btn-sm btn-comman mr-1 mb-1"
                            >
                              Download
                            </a>
                            {r.receipt && (
                              <a
                                href={r.receipt}
                                target="_blank"
                                rel="noreferrer"
                                className="btn btn-sm btn-success mb-1"
                              >
                                Fee Slip
                              </a>
                            )}
                          </>
                        ) : Number(r.status) === 2 ? (
                          <span className="text-danger">{r.reject_reason || "Rejected"}</span>
                        ) : (
                          "--"
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
};

export default StudentNoc;
